import { useMemo, useRef, useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

/**
 * Rotation cadence for the suggestions loading copy.
 *
 * Mirrors `ROTATION_INTERVAL_MS` inside `RovoThinkingBar`, which the inline loading bar
 * delegates its rotation to. Both sidebar surfaces derive their message from the shared
 * `thinkingStartedAt` anchor at this cadence, so whichever one is on screen shows the same
 * message for the same elapsed time — and switching between them mid-review continues the
 * rotation instead of restarting it.
 */
export const LOADING_ROTATION_INTERVAL_MS = 4000;

const i18n = defineMessages({
	analyzingWithAgent: {
		id: 'suggestions-panel.loading-messages.analyzing-content-with-agent',
		defaultMessage: '{agentName} is analyzing',
		description:
			'First loading message shown in the suggestions panel while an agent generates suggestions. {agentName} is the name of the agent doing the review, for example "Content Reviewer".',
	},
	// Fallback for the message above, used when the reviewing agent has not resolved.
	reviewing: {
		id: 'suggestions-panel.loading-messages.reviewing',
		defaultMessage: 'Rovo is reviewing your page',
		description:
			'First loading message shown in the suggestions panel while Rovo generates suggestions, when the name of the reviewing agent is unknown',
	},
	mayTakeAFewMinutes: {
		id: 'suggestions-panel.loading-messages.may-take-a-few-minutes',
		defaultMessage: 'This may take a few minutes',
		description: 'Loading message shown in the suggestions panel while generating suggestions',
	},
	gatheringContext: {
		id: 'suggestions-panel.loading-messages.gathering-context',
		defaultMessage: 'Gathering context',
		description: 'Loading message shown in the suggestions panel while generating suggestions',
	},
	reviewingConsistency: {
		id: 'suggestions-panel.loading-messages.reviewing-consistency',
		defaultMessage: 'Reviewing consistency',
		description: 'Loading message shown in the suggestions panel while generating suggestions',
	},
	checkingWritingClarity: {
		id: 'suggestions-panel.loading-messages.checking-writing-clarity',
		defaultMessage: 'Checking writing clarity',
		description: 'Loading message shown in the suggestions panel while generating suggestions',
	},
	identifyingImprovements: {
		id: 'suggestions-panel.loading-messages.identifying-improvements',
		defaultMessage: 'Identifying improvements',
		description: 'Loading message shown in the suggestions panel while generating suggestions',
	},
	generatingSuggestions: {
		id: 'suggestions-panel.loading-messages.generating-suggestions',
		defaultMessage: 'Generating AI suggestions',
		description: 'Loading message shown in the suggestions panel while generating suggestions',
	},
	refiningSuggestions: {
		id: 'suggestions-panel.loading-messages.refining-suggestions',
		defaultMessage: 'Refining AI suggestions',
		description: 'Loading message shown in the suggestions panel while generating suggestions',
	},
	prioritizingSuggestions: {
		id: 'suggestions-panel.loading-messages.prioritizing-suggestions',
		defaultMessage: 'Prioritizing AI suggestions',
		description: 'Loading message shown in the suggestions panel while generating suggestions',
	},
});

/**
 * The rotating loading copy for the suggestions panel, in order, shared by both sidebar
 * loading surfaces so they can't drift apart.
 *
 * The first message names the reviewing agent when one is known, falling back to the generic
 * Rovo wording. The trailing ellipsis each message is shown with is added by the surface
 * rendering it, not here.
 */
export function useSuggestionsLoadingMessages({
	agentName,
}: { agentName?: string } = {}): string[] {
	const { formatMessage } = useIntl();

	return useMemo(
		() => [
			agentName
				? formatMessage(i18n.analyzingWithAgent, { agentName })
				: formatMessage(i18n.reviewing),
			formatMessage(i18n.mayTakeAFewMinutes),
			formatMessage(i18n.gatheringContext),
			formatMessage(i18n.reviewingConsistency),
			formatMessage(i18n.checkingWritingClarity),
			formatMessage(i18n.identifyingImprovements),
			formatMessage(i18n.generatingSuggestions),
			formatMessage(i18n.refiningSuggestions),
			formatMessage(i18n.prioritizingSuggestions),
		],
		[agentName, formatMessage],
	);
}

/**
 * Picks the message to show for the current point in a thinking session, advancing every
 * `LOADING_ROTATION_INTERVAL_MS` and settling on the last one.
 *
 * `startedAt` (epoch ms, from the plugin's shared thinking state) anchors the sequence, so a
 * surface that mounts mid-review joins the rotation where it already is rather than starting
 * over. Without it the rotation starts from the first message on mount.
 *
 * This deliberately matches `RovoThinkingBar`'s internal `useSequentialText`, which the inline
 * loading bar uses — that hook isn't exported, so the behaviour is reproduced rather than
 * shared. Keep the two aligned.
 */
export function useRotatingLoadingMessage(messages: string[], startedAt?: number | null): string {
	const lastIndex = Math.max(0, messages.length - 1);

	const indexFromStart = (): number => {
		if (startedAt == null) {
			return 0;
		}
		const elapsed = Math.max(0, Date.now() - startedAt);
		return Math.min(lastIndex, Math.floor(elapsed / LOADING_ROTATION_INTERVAL_MS));
	};

	const [index, setIndex] = useState(indexFromStart);
	const indexRef = useRef(index);
	indexRef.current = index;

	useEffect(() => {
		if (indexRef.current >= lastIndex) {
			return;
		}

		const interval = setInterval(() => {
			const next = indexRef.current + 1;
			indexRef.current = next;
			setIndex(next);
			if (next >= lastIndex) {
				clearInterval(interval);
			}
		}, LOADING_ROTATION_INTERVAL_MS);

		return () => clearInterval(interval);
	}, [lastIndex, startedAt]);

	return messages[Math.min(index, lastIndex)] ?? '';
}
